import React from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import { useTileForm } from './useTileForm';

import {
  DirectionalButtonLeft,
  DirectionalButtonRight,
  ArrowWrapper,
} from '../FullSizeTile/components/DirectionalButtons.jsx';
import HeadlineInput from './components/HeadlineInput.jsx';
import Attachments from '../Attachments/Attachments.jsx';
import DisclaimerText from './components/DisclaimerText.jsx';
import TileSettings from './components/TileSettings.jsx';
import SelectDropdown from './components/SelectDropdown.jsx';
import Description from './components/Description.jsx';
import MediaUploader from './components/MediaUploader.jsx';
import Interaction from './components/Interaction/Interaction.jsx';
import { PointsSlider } from './components/Slider.jsx';
import AnonymousBannerComponent from '../FullSizeTile/components/AnonymousBannerComponent.jsx';
import Hr from '../Hr';

import { InteractionContext } from './contexts';
import { useGetAudienceAndRibbonTag } from './data';

import { TileActionMenu } from '../TileActionMenu/TileActionMenu.jsx';

const TileForm = ({
  tile,
  onSave,
  onClose,
  postId,
  goToPreview,
  nextTile,
  prevTile,
  isFirstTile,
  isLastTile,
  arrowRightRef,
  arrowLeftRef,
  position,
}) => {
  const {
    values,
    handleChange,
    inputError,
    handleBlur,
    addAttachment,
    removeAttachment,
    handleShowDisclaimer,
    handleQuestionSubTypeOnChange,
    handleQuestionOnChange,
    handleAddAnswer,
    handleRemoveAnswer,
    handleMediaChange,
    handlePoints,
    removeMedia,
    admins,
    errors,
    setInitialValues,
    saveAndNavigate,
    autoSaved,
  } = useTileForm({ tile, onSave, postId, position });

  const tileAudienceAndRibbon = useGetAudienceAndRibbonTag();
  const { audiences } = tileAudienceAndRibbon;
  const ribbonTags = tileAudienceAndRibbon.ribbon_tags;
  const hasRibbonTags = ribbonTags && ribbonTags.length > 0;
  const hasAudiences = audiences && audiences.length > 0;

  const { t } = useTranslation('tileForm');
  const { isAnonymous } = values;

  return (
    <>
      <TileActionMenu
        currentPage="edit"
        onClose={() => saveAndNavigate(onClose)}
        onDiscard={onClose}
        uuid={values.id}
        goToPreview={() => saveAndNavigate(goToPreview)}
        campaignContainer={values.campaignContainer}
        publishDate={values.publishDate}
        tileStatus={values.status}
        postId={values.postId}
        autoSaved={autoSaved}
      />
      <FormWrapper>
        <MediaUploader
          values={values}
          handleChange={handleMediaChange}
          removeMedia={removeMedia}
        />
        <FormSection>
          <HeadlineInput
            name="headline"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.headline}
            error={inputError('headline')}
          />
          <Hr />
          <Description
            name="supportingContent"
            onChange={handleChange}
            onInitialValue={normalizedContent => {
              setInitialValues(prevState => {
                return { ...prevState, supportingContent: normalizedContent };
              });
            }}
            value={values.supportingContent}
            error={errors.supportingContent}
          />
          <Hr />
          <DisclaimerText
            toggleName="showDisclaimerText"
            textName="disclaimerText"
            handleChange={handleChange}
            handleShowDisclaimer={handleShowDisclaimer}
            showDisclaimerText={
              values.showDisclaimerText || !!values.disclaimerText
            }
            disclaimerText={values.disclaimerText}
          />
          <Attachments
            attachments={values.attachments}
            removeAttachment={removeAttachment}
            addAttachment={addAttachment}
          />
          {isAnonymous && <AnonymousBannerComponent margin="24px 0" />}
          <InteractionContext.Provider
            value={{
              values,
              handleChange,
              handleAddAnswer,
              handleRemoveAnswer,
              handleQuestionSubTypeOnChange,
              handleQuestionOnChange,
              admins,
              errors,
            }}
          >
            <Interaction />
          </InteractionContext.Provider>
          <Hr />
          <PointsSlider points={values.points} handleChange={handlePoints} />
          <Hr />
          <TileSettings
            isSharable={values.isSharable}
            isPublic={values.isPublic}
            handleChange={handleChange}
          />
          {hasAudiences && (
            <React.Fragment>
              <Hr />
              <SelectDropdown
                title={t('audience.title')}
                name="audienceId"
                handleChange={handleChange}
                currentValue={values.audienceId}
                options={audiences}
                showUserCount={true}
              />
            </React.Fragment>
          )}
          {hasRibbonTags && (
            <React.Fragment>
              <Hr />
              <SelectDropdown
                title={t('topic.title')}
                name="ribbonTagId"
                handleChange={handleChange}
                currentValue={values.ribbonTagId}
                options={ribbonTags}
                showUserCount={false}
              />
            </React.Fragment>
          )}

          <ArrowWrapper $hasSidePadding={false}>
            {prevTile && (
              <DirectionalButtonLeft
                prevTile={e => {
                  e.preventDefault();
                  saveAndNavigate(prevTile);
                }}
                ref={arrowLeftRef}
                hidden={isFirstTile}
              />
            )}
            {nextTile && (
              <DirectionalButtonRight
                nextTile={e => {
                  e.preventDefault();
                  saveAndNavigate(nextTile);
                }}
                ref={arrowRightRef}
                hidden={isLastTile}
              />
            )}
          </ArrowWrapper>
        </FormSection>
      </FormWrapper>
    </>
  );
};

export default TileForm;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 600px;
  border-radius: 8px;
  position: relative;
  margin: auto;
  margin-top: 20px;
  background: ${themeGet('colors.white')};
`;

const FormSection = styled.section`
  padding: 0 24px;
`;
